import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Container,
  Group,
  Loader,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import PageHeader from '@components/shared/PageHeader';
import {
  legacyUserDetailQueryKey,
  useLegacyUserDetailQuery,
} from '../hooks/queries/useLegacyUserDetailQuery';
import {
  LegacyKeyValueSection,
  LegacyRowsSection,
  LegacyStatsSection,
  LegacyTestAnswersSection,
  LegacyBillingSection,
  LegacyDevicesSection,
} from './components/LegacyUserSections';
import { LegacyMigrationVerificationResult } from './components/LegacyMigrationVerificationResult';
import {
  updateLegacyUserMigrationStatus,
  migrateLegacyUser,
  verifyLegacyUserMigration,
  exportLegacyUserMigrationJson,
  migrateLegacyUserFromJson,
  saveLegacyUserMigrationExport,
  loadLocalMigrationExport,
  type LegacyMigrationVerification,
  type LegacyUserMigrationExportPayload,
} from '../services/legacyUserManager';

export default function AdminLegacyUserDetail() {
  const { id } = useParams();
  const userId = id ? Number(id) : null;
  const queryClient = useQueryClient();
  const { data, isLoading, error, refetch } = useLegacyUserDetailQuery(
    userId && Number.isFinite(userId) ? userId : null
  );
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [verification, setVerification] =
    useState<LegacyMigrationVerification | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [migratingFromJson, setMigratingFromJson] = useState(false);

  const titleName =
    (data?.bes_users?.full_name as string | undefined) ||
    ([data?.bes_users?.first_name, data?.bes_users?.last_name]
      .filter(Boolean)
      .join(' ') || undefined) ||
    (data?.user_info
      ? ([data.user_info.name, data.user_info.surname].filter(Boolean).join(' ') ||
          undefined)
      : undefined) ||
    (data?.user?.email as string | undefined) ||
    `User #${id}`;

  const migrationStatus = data?.migration?.status ?? 'pending';
  const isIgnored = migrationStatus === 'ignored';
  const isMigrated = migrationStatus === 'migrated';

  const refresh = async () => {
    if (!userId) return;
    await queryClient.invalidateQueries({
      queryKey: legacyUserDetailQueryKey(userId),
    });
    await refetch();
  };

  const handleSetStatus = async (status: 'ignored' | 'pending') => {
    if (!userId) return;
    setSaving(true);
    try {
      await updateLegacyUserMigrationStatus(
        userId,
        status,
        note.trim() || null
      );
      notifications.show({
        color: 'green',
        message:
          status === 'ignored'
            ? 'User marked as ignored (will not migrate)'
            : isMigrated
              ? 'Re-opened — Preview / Migrate enabled again'
              : 'Ignore removed — user back to migrate queue',
      });
      await refresh();
    } catch (err) {
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Failed to update status',
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    if (!userId) return;
    window.open(
      `/admin/legacy-users/${userId}/preview`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleMigrate = async () => {
    if (!userId) return;
    if (
      !window.confirm(
        `Apply migration for user #${userId}? This writes UserCourses / UserProducts on be2025.`
      )
    ) {
      return;
    }
    setMigrating(true);
    try {
      const result = await migrateLegacyUser(userId, { dryRun: false });
      if (!result.dryRun) {
        notifications.show({
          color: 'green',
          message: `Migrated → be2025 user ${result.applied.new_user_id}`,
        });
      }
      await refresh();
    } catch (err) {
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Migration failed',
      });
      await refresh();
    } finally {
      setMigrating(false);
    }
  };

  const handleVerify = async () => {
    if (!userId) return;
    setVerifying(true);
    setVerification(null);
    try {
      let exportPayload: LegacyUserMigrationExportPayload | null = null;
      let exportPath: string | null = null;
      try {
        const local = await loadLocalMigrationExport(userId);
        exportPayload = local.payload;
        exportPath = local.path;
      } catch {
        throw new Error(
          'Export JSON mancante in migration/v1 — premi Export JSON prima di Verify'
        );
      }

      const result = await verifyLegacyUserMigration(userId, { exportPayload });
      setVerification(result);

      const vaporOk = result.vapor_to_json?.ok ?? false;
      const color =
        result.status === 'pass' && vaporOk
          ? 'teal'
          : result.status === 'not_linked' && vaporOk
            ? 'blue'
            : 'red';

      notifications.show({
        color,
        message:
          result.status === 'pass' && vaporOk
            ? `Verify PASS · vapor→json OK · json→be OK · ${exportPath}`
            : !vaporOk
              ? `Verify FAIL vapor→json · ${result.vapor_to_json?.summary.mismatches ?? 0} mismatch(es) · ${exportPath}`
              : result.status === 'not_linked'
                ? 'vapor→json OK · json→be: no be2025 user yet — Migrate from JSON first'
                : result.status === 'plan_failed'
                  ? `vapor→json OK · json→be plan failed: ${result.plan_error}`
                  : `Verify FAIL json→be · ${result.summary.mismatches} mismatch(es)`,
      });

    } catch (err) {
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Verify failed',
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleExportJson = async () => {
    if (!userId) return;
    setExporting(true);
    try {
      const payload = await exportLegacyUserMigrationJson(userId);
      const saved = await saveLegacyUserMigrationExport(payload);
      notifications.show({
        color: 'blue',
        message:
          saved.mode === 'filesystem'
            ? `Saved to ${saved.path}`
            : `Downloaded ${saved.path} (move into repo migration/v1 if needed)`,
      });
    } catch (err) {
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Export failed',
      });
    } finally {
      setExporting(false);
    }
  };

  const handleMigrateFromJsonFile = async (file: File | null) => {
    if (!userId) return;
    setMigratingFromJson(true);
    try {
      let parsed: LegacyUserMigrationExportPayload;
      let sourceLabel: string;

      if (file) {
        const text = await file.text();
        try {
          parsed = JSON.parse(text) as LegacyUserMigrationExportPayload;
        } catch {
          throw new Error('Selected file is not valid JSON');
        }
        sourceLabel = file.name;
      } else {
        const local = await loadLocalMigrationExport(userId);
        parsed = local.payload;
        sourceLabel = local.path;
      }

      if (Number(parsed.legacy_user_id) !== userId) {
        throw new Error(
          `JSON legacy_user_id=${parsed.legacy_user_id} does not match page user #${userId}`
        );
      }

      if (
        !window.confirm(
          `Migrate from JSON (${sourceLabel}) for #${parsed.legacy_user_id} (${parsed.email})?\nUses JSON only — not live vapor DB.`
        )
      ) {
        return;
      }

      const result = await migrateLegacyUserFromJson(parsed, { dryRun: false });
      if (!result.dryRun) {
        notifications.show({
          color: 'green',
          message: `Migrated from JSON → be2025 user ${result.applied.new_user_id}`,
        });
      }
      await refresh();
    } catch (err) {
      notifications.show({
        color: 'red',
        message: (err as Error).message || 'Migrate from JSON failed',
      });
      await refresh();
    } finally {
      setMigratingFromJson(false);
    }
  };

  const statusBadge = () => {
    if (isIgnored) {
      return (
        <Badge color='gray' variant='filled'>
          ignored
        </Badge>
      );
    }
    if (isMigrated) {
      return (
        <Badge color='teal' variant='light'>
          migrated
          {data?.migration?.new_user_id
            ? ` → #${data.migration.new_user_id}`
            : ''}
        </Badge>
      );
    }
    if (migrationStatus === 'failed') {
      return (
        <Badge color='red' variant='light'>
          failed
        </Badge>
      );
    }
    if (migrationStatus === 'dry_run') {
      return (
        <Badge color='violet' variant='light'>
          dry_run
        </Badge>
      );
    }
    return (
      <Badge color='blue' variant='light'>
        to migrate
      </Badge>
    );
  };

  return (
    <Container size='2xl' py='lg'>
      <Stack gap='lg'>
        <PageHeader
          title={titleName}
          subtitle={`Legacy vapor user #${id} — read-only${
            data?.vapor_source?.database
              ? ` · DB ${data.vapor_source.database}`
              : ''
          }`}
          breadcrumbs={[
            { label: 'Admin', href: '/admin/users' },
            { label: 'Legacy Migration', href: '/admin/legacy-migration' },
            { label: 'Legacy users', href: '/admin/legacy-users' },
            { label: String(id ?? '') },
          ]}
          backUrl='/admin/legacy-users'
          actions={
            <Group>
              {statusBadge()}
              {data?.user?.deleted_at ? (
                <Badge color='red'>soft-deleted</Badge>
              ) : null}
            </Group>
          }
        />

        {isLoading ? (
          <Group justify='center' py='xl'>
            <Loader />
          </Group>
        ) : error ? (
          <Text c='red'>{(error as Error).message}</Text>
        ) : !data ? (
          <Text c='dimmed'>User not found</Text>
        ) : (
          <Stack gap='md'>
            <Alert color='yellow' title='Staging vapor ≠ production old admin'>
              Legacy legge RDS staging `vapor` (`OLD_DB_*`). Per alcuni utenti
              (es. devices / configurations / patients / answers) le righe ci
              sono su prod v1 ma non su staging — la pagina mostra 0 rows anche
              se l’admin vapor di produzione le vede. Per inventariare i dati
              reali punta `OLD_DB_*` al MySQL vapor di produzione (read-only).
            </Alert>

            <Stack gap='sm'>
              <Title order={3}>Migration decision</Title>
              <Text size='sm' c='dimmed'>
                Stored on be2025 (`UserMigrationLog`). Vapor data is never
                modified.
              </Text>
              {data.migration?.note ? (
                <Text size='sm'>
                  Note: {data.migration.note}
                </Text>
              ) : null}
              <Textarea
                placeholder='Optional note (why ignore / context)'
                value={note}
                onChange={(event) => setNote(event.currentTarget.value)}
                minRows={2}
              />
              <Group>
                <Button
                  color='teal'
                  variant='light'
                  disabled={isIgnored || isMigrated}
                  onClick={handlePreview}
                >
                  Preview
                </Button>
                <Button
                  color='teal'
                  loading={migrating}
                  disabled={isIgnored || isMigrated}
                  onClick={handleMigrate}
                >
                  Migrate
                </Button>
                <Button
                  color='blue'
                  variant='light'
                  loading={exporting}
                  onClick={handleExportJson}
                >
                  Export JSON
                </Button>
                <Button
                  color='blue'
                  variant='outline'
                  loading={migratingFromJson}
                  disabled={isIgnored || isMigrated}
                  onClick={() => {
                    void (async () => {
                      try {
                        await loadLocalMigrationExport(userId!);
                        await handleMigrateFromJsonFile(null);
                      } catch {
                        document
                          .getElementById(`migrate-json-file-${userId}`)
                          ?.click();
                      }
                    })();
                  }}
                >
                  Migrate from JSON
                </Button>
                <input
                  id={`migrate-json-file-${userId}`}
                  type='file'
                  accept='application/json,.json'
                  hidden
                  onChange={(event) => {
                    const file = event.currentTarget.files?.[0] ?? null;
                    event.currentTarget.value = '';
                    void handleMigrateFromJsonFile(file);
                  }}
                />
                <Button
                  color='violet'
                  variant='light'
                  loading={verifying}
                  onClick={handleVerify}
                >
                  Verify mapping
                </Button>
                {isMigrated ? (
                  <Button
                    variant='light'
                    color='orange'
                    loading={saving}
                    onClick={() => {
                      if (
                        !window.confirm(
                          'Re-open this user for migrate? Status goes back to pending (be2025 data is kept; Migrate will merge again).'
                        )
                      ) {
                        return;
                      }
                      void handleSetStatus('pending');
                    }}
                  >
                    Re-open migrate
                  </Button>
                ) : isIgnored ? (
                  <Button
                    variant='light'
                    loading={saving}
                    onClick={() => handleSetStatus('pending')}
                  >
                    Undo ignore
                  </Button>
                ) : (
                  <Button
                    color='gray'
                    loading={saving}
                    onClick={() => handleSetStatus('ignored')}
                  >
                    Mark as ignored
                  </Button>
                )}
              </Group>
              {isMigrated ? (
                <Text size='sm' c='orange'>
                  Already migrated
                  {data.migration?.new_user_id
                    ? ` → be2025 #${data.migration.new_user_id}`
                    : ''}
                  . Use “Re-open migrate” to enable Preview / Migrate again
                  (e.g. to fix created_at / legacy_source).
                </Text>
              ) : null}
              {verification ? (
                <LegacyMigrationVerificationResult verification={verification} />
              ) : null}
            </Stack>

            <Title order={3}>Identity</Title>
            <LegacyKeyValueSection title='users (Laravel auth)' data={data.user} />
            <LegacyKeyValueSection
              title='Users (BES profile, capital U)'
              data={data.bes_users ?? null}
              emptyLabel='No matching row in Users'
            />
            <LegacyKeyValueSection title='user_info' data={data.user_info} />
            <LegacyRowsSection title='customer_data' rows={data.customer_data} />
            <LegacyRowsSection title='professionals' rows={data.professionals} />
            <LegacyRowsSection title='roles (Spatie)' rows={data.roles} />
            <LegacyRowsSection title='teams' rows={data.teams} />

            <Title order={3}>Billing</Title>
            <LegacyBillingSection
              subscriptions={data.subscriptions}
              subscriptionItems={data.subscription_items}
              paymentIntents={data.payment_intents}
            />

            <Title order={3}>Clinical / Polar</Title>
            <LegacyRowsSection title='patients' rows={data.patients} emptyLabel={
              '0 patients on staging vapor (OLD_DB). Production may have patients + linked configurations.'
            } />
            <LegacyStatsSection
              title='patient-linked counts'
              stats={{
                configurations: data.patient_stats.configurations_count,
                a_time_events: data.patient_stats.a_time_events_count,
                food_results: data.patient_stats.food_results_count,
              }}
            />
            <LegacyRowsSection
              title='configurations'
              rows={data.configurations ?? []}
              emptyLabel='0 configurations on staging vapor (OLD_DB). Production may have rows linked by patient_id / user_id / user_device_id.'
            />
            <LegacyRowsSection
              title={
                data.a_time_events_truncated
                  ? `a_time_events (truncated, showing ${data.a_time_events?.length ?? 0} of ${data.patient_stats.a_time_events_count})`
                  : 'a_time_events'
              }
              rows={data.a_time_events ?? []}
            />
            <Text size='sm' c='dimmed'>
              `food` is catalog. Lists link by `food_lists.user_id`; items via
              `food_list_food` (+ food_name); results via
              `food_results.patient_id` → patients.
            </Text>
            <LegacyRowsSection
              title='food_lists (+ items_count)'
              rows={data.food_lists}
            />
            <LegacyRowsSection
              title='food_list_food (+ food_name)'
              rows={data.food_list_food ?? []}
            />
            <LegacyRowsSection
              title='food_results (+ food_name)'
              rows={data.food_results ?? []}
            />

            <Title order={3}>Devices</Title>
            <LegacyDevicesSection
              devices={data.devices}
              connections={data.device_connections}
              userEmail={
                typeof data.user?.email === 'string' ? data.user.email : null
              }
            />

            <Title order={3}>Courses & media</Title>
            <Text size='sm' c='dimmed'>
              Same relations as become-fe-2023 UserModal: entitlements in
              `user_has_courses` (Questions?/Feedback?/Attempts), videos in
              `user_has_videos`, answers via `user_answers` (old
              retrieveCourseTests).
            </Text>
            <LegacyRowsSection
              title={`user_has_courses (Questions?/Feedback?/Attempts) — ${data.courses?.length ?? 0}`}
              rows={data.courses}
            />
            <LegacyTestAnswersSection
              rows={data.user_answers}
              courses={data.courses}
            />
            <LegacyRowsSection
              title={`courses_linked (${data.courses_linked?.length ?? 0} distinct)`}
              rows={data.courses_linked ?? []}
            />
            <LegacyRowsSection
              title='user_course_progress'
              rows={data.course_progress}
            />
            <LegacyRowsSection
              title='user_course_progress_old'
              rows={data.course_progress_old ?? []}
            />
            <LegacyRowsSection
              title='feedback (+ course_name)'
              rows={data.feedback}
            />
            <LegacyRowsSection
              title={`course_tests — ${data.course_tests?.length ?? 0} rows`}
              rows={data.course_tests ?? []}
            />
            <LegacyRowsSection
              title={`user_has_videos (+ video_name) — ${data.videos?.length ?? 0}`}
              rows={data.videos}
            />

            <Title order={3}>Other linked rows</Title>
            <LegacyRowsSection
              title='page_views'
              rows={data.page_views ?? []}
            />
            <LegacyRowsSection
              title='personal_access_tokens (token redacted)'
              rows={data.personal_access_tokens ?? []}
            />
          </Stack>
        )}
      </Stack>
    </Container>
  );
}
